import { HeadingSection } from "@/components/heading/HeadingSection";
import { useModals } from "@/features/modals/hooks";
import { type ModalDefinition } from "@/features/modals/types";
import { getRole } from "@/features/team/utils";
import { type UpdateTeamPayload } from "@repo/api";
import { useIsDevOrSuperUser, useProfile } from "@repo/api";
import {
  createTeam,
  deleteTeam,
  loadTeam,
  updateTeam,
  useMyTeams,
} from "@repo/api";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import { useSettings } from "@repo/ui";
import { useToast } from "@repo/ui";
import { ModalSheet } from "@repo/ui";
import { useTypesafeActions } from "@repo/utils";
import { trackAnalytics } from "@repo/utils";
import { assertUnreachable, getErrorMessage } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { type ReactElement, useEffect } from "react";

import { CreateTeamStep } from "./CreateTeamStep";
import {
  type ManageTeamState,
  type ManageTeamStateActions,
  ManageTeamStateDefault,
  ManageTeamStateReducer,
  manageTeamStateActions,
} from "./ManageTeamReducer";
import { SelectTeamStep } from "./SelectTeamStep";
import { TeamDetailStep } from "./TeamDetailsStep";

interface ManageTeamModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onUpdate?: () => void;
}

const getView = (
  state: ManageTeamState,
  actions: ManageTeamStateActions,
  onCreateTeam: () => void,
  onUpdateTeam: () => void,
  onDeleteTeam: (teamId: number) => void,
  pushModal: (modal: ModalDefinition) => void,
  onClose: () => void
): {
  title: string;
  view: ReactElement;
  onBack: (() => void) | undefined;
  footer: ReactElement | undefined;
} => {
  const reset = () => {
    actions.setEditMode(false);
    actions.setNewTeam(undefined);
    actions.setUpdatedTeam(undefined);
    actions.setInitialTeamMemberIds([]);
  };
  switch (state.currentStep) {
    case "SELECT":
      return {
        title: t("action.manage_team"),
        view: <SelectTeamStep state={state} actions={actions} />,
        onBack: onClose,
        footer: (
          <Button
            type="primary"
            text={t("action.create_team")}
            onClick={() => {
              actions.setStep("CREATE");
              actions.setEditMode(true);
              trackAnalytics("open_create_team_modal");
            }}
          />
        ),
      };

    case "DETAIL":
      return {
        title: t("label.team_details"),
        view: <TeamDetailStep state={state} actions={actions} />,
        onBack: () => {
          actions.setStep("SELECT");
          reset();
          trackAnalytics("back_from_team_details_modal");
        },
        footer: (
          <>
            {state.editMode ? (
              <Button
                text={t("action.save")}
                onClick={() => {
                  onUpdateTeam();
                  trackAnalytics("save_updated_team_details");
                }}
                type="primary"
                loading={state.loadingState === "UPDATING"}
                disabled={
                  !state.updatedTeam?.teamName ||
                  state.updatedTeam.description.length > 120 ||
                  !state.updatedTeam.buCode ||
                  isEmpty(state.updatedTeam.hfbs)
                }
              />
            ) : (
              <Button
                text={t("action.edit")}
                type="primary"
                disabled={state.role !== "MANAGER" && state.role !== "MEMBER"}
                // TODO: fix error
                onClick={async () => {
                  if (!state.editMode) {
                    let team = state.teams.find(
                      (it) => it.teamId === state.selectedTeamId
                    );
                    if (state.selectedTeamId && !team) {
                      team = await loadTeam(state.selectedTeamId);
                    }

                    if (team) {
                      actions.setUpdatedTeam(team);
                      actions.setInitialTeamMemberIds(
                        team.teamMembers.map((member) => member.emailId)
                      );
                      actions.setEditMode(!state.editMode);
                    } else {
                      // eslint-disable-next-line no-console
                      console.error("Team not found");
                    }
                  }
                  trackAnalytics("edit_team_details");
                }}
              />
            )}

            <Button
              text={t("action.delete_team")}
              disabled={state.role !== "MANAGER"}
              onClick={() => {
                pushModal({
                  type: "CONFIRM",
                  onConfirm: () => onDeleteTeam(state.selectedTeamId!),
                  title: t("action.delete_team"),
                  message: t("prompt.confirm_delete_team"),
                });
                trackAnalytics("delete_team");
              }}
              loading={state.loadingState === "DELETING"}
            />
          </>
        ),
      };

    case "CREATE":
      return {
        title: t("action.create_team"),
        view: <CreateTeamStep actions={actions} />,
        onBack: () => {
          actions.setStep("SELECT");
          reset();
          trackAnalytics("back_from_create_team_modal");
        },
        footer: (
          <Button
            text={t("action.create")}
            type="primary"
            onClick={() => {
              onCreateTeam();
              trackAnalytics("create_new_team");
            }}
            loading={state.loadingState === "CREATING"}
            disabled={
              !state.newTeam?.teamName ||
              state.newTeam?.teamDescription.length > 120 ||
              !state.newTeam?.buCode ||
              isEmpty(state.newTeam?.hfbs) ||
              !state.newTeam?.managerEmailId
            }
          />
        ),
      };
  }
  assertUnreachable(state.currentStep);
};

export const ManageTeamModal = ({
  isOpen,
  closeModal,
  onUpdate,
}: ManageTeamModalProps) => {
  const { data: myTeams, isValidating } = useMyTeams();
  const { updateToast } = useToast();
  const { market } = useSettings();
  const { pushModal } = useModals();
  const { data: profile } = useProfile();
  const isDevOrSuperUser = useIsDevOrSuperUser();

  const [state, actions] = useTypesafeActions<
    ManageTeamState,
    ManageTeamStateActions
  >(ManageTeamStateReducer, ManageTeamStateDefault, manageTeamStateActions);

  useEffect(() => {
    if (!isValidating && myTeams) {
      actions.setTeams(myTeams);
    }
  }, [isValidating, myTeams, actions]);

  useEffect(() => {
    if (state.selectedTeamId) {
      actions.setRole(
        getRole(
          state.teams,
          state.selectedTeamId,
          profile?.mail,
          isDevOrSuperUser
        )
      );
    }
    // Only update role when a new team is selected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedTeamId]);

  const onUpdateTeam = async () => {
    if (!state.updatedTeam || !state.selectedTeamId) {
      return;
    }

    actions.setLoadingState("UPDATING");

    try {
      const payload: UpdateTeamPayload = {
        teamName: state.updatedTeam.teamName,
        default: state.updatedTeam.default,
        teamDescription: state.updatedTeam.description,
        addTeamMembers: state.updatedTeam.teamMembers
          .filter(
            (member) => !state.initialTeamMemberIds.includes(member.emailId)
          )
          .map((el) => el.emailId),
        removeTeamMembers: state.initialTeamMemberIds.filter(
          (id) =>
            !state.updatedTeam?.teamMembers.find(
              (member) => member.emailId === id
            )
        ),
        countryCode: market.toUpperCase(),
        buCode: state.updatedTeam.buCode,
        hfbs: state.updatedTeam.hfbs,
        managerId: state.updatedTeam.manager.emailId,
      };

      await updateTeam(state.selectedTeamId, payload);
      onUpdate?.();
      updateToast({ id: state.selectedTeamId, title: t("prompt.team_saved") });
    } catch (err) {
      updateToast({
        id: state.selectedTeamId,
        title: t("prompt.team_saved_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      actions.setEditMode(false);
      actions.setLoadingState(undefined);
    }
  };

  const onCreateTeam = async () => {
    if (!state.newTeam) {
      return;
    }
    actions.setLoadingState("CREATING");
    try {
      await createTeam(state.newTeam);

      updateToast({ title: t("prompt.team_saved") });
    } catch (err) {
      updateToast({
        title: t("prompt.team_saved_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      actions.setEditMode(false);
      actions.setLoadingState(undefined);
      actions.setStep("SELECT");
    }
  };

  const onDeleteTeam = async (teamId: number) => {
    actions.setLoadingState("DELETING");
    if (!teamId) {
      return;
    }
    try {
      await deleteTeam(teamId);
      updateToast({ id: teamId, title: t("prompt.team_deleted") });
    } catch (err) {
      updateToast({
        id: teamId,
        title: t("prompt.team_deleted_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      actions.setLoadingState(undefined);
      actions.setStep("SELECT");
    }
  };

  const onClose = () => {
    actions.setStep("SELECT");
    actions.setSelectedTeamId(undefined);
    actions.setUpdatedTeam(undefined);
    actions.setInitialTeamMemberIds([]);
    closeModal();
  };

  const { title, view, onBack, footer } = getView(
    state,
    actions,
    // TODO: fix error

    onCreateTeam,

    onUpdateTeam,

    onDeleteTeam,
    pushModal,
    onClose
  );
  return (
    <ModalSheet
      title={title}
      visible={isOpen}
      onClose={onClose}
      onBack={onBack}
      footer={footer}
    >
      <HeadingSection value={3}>{view}</HeadingSection>
    </ModalSheet>
  );
};
